import { createStore } from "vuex";
import tenancyStore from "@devcentre/tenancy-components/src/store/module.js";
import APIService from "@/services/APIService";

const TIER_LIMITS = {
  freemium: { boards: 3, members: 1, rulesPerBoard: 3 },
  professional: { boards: 20, members: 20, rulesPerBoard: Infinity },
  business: { boards: 100, members: 100, rulesPerBoard: Infinity },
  enterprise: { boards: Infinity, members: Infinity, rulesPerBoard: Infinity },
};

const TIER_ORDER = ["freemium", "professional", "business", "enterprise"];

export default createStore({
  modules: {
    tenancy: tenancyStore,
  },
  state: {
    tenantDetails: null,
    boardCount: 0,
  },
  getters: {
    currentRole: (state) => {
      void state.tenancy.currentTenant;
      try {
        const token = sessionStorage.getItem("auth_token");
        if (!token) return null;
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.role || null;
      } catch {
        return null;
      }
    },
    tenantTier: (_state, getters) => {
      const appCode = process.env.VUE_APP_APP_CODE || "wa";
      const products = getters["tenancy/currentUser"]?.products || [];
      const kanban = products.filter((p) => p.app === appCode);
      return kanban.reduce((best, p) => {
        return TIER_ORDER.indexOf(p.level) > TIER_ORDER.indexOf(best) ? p.level : best;
      }, "freemium");
    },
    tierLimits: (_state, getters) => {
      return TIER_LIMITS[getters.tenantTier] || TIER_LIMITS.freemium;
    },
    boardCount: (state) => state.boardCount,
    canCreateBoard: (_state, getters) => {
      return getters.boardCount < getters.tierLimits.boards;
    },
    boardsRemaining: (_state, getters) => {
      const remaining = getters.tierLimits.boards - getters.boardCount;
      return remaining === Infinity ? Infinity : Math.max(0, remaining);
    },
    tenantDetails: (state) => state.tenantDetails,
    getTableViewType: () => (table) => {
      if (table?.metadata?.view_type) return table.metadata.view_type;
      if (table?.table_type === "process") return "kanban";
      if (table?.table_type === "lookup") return "lookup";
      return "table";
    },
    getTableVisibility: () => (table) => table?.metadata?.visibility || "member",
    canSeeTable: (_state, getters) => (table) => {
      const visibility = getters.getTableVisibility(table);
      const role = getters.currentRole;
      if (role === "admin") return true;
      if (role === "member") return visibility !== "admin";
      if (role === "viewer") return visibility === "viewer";
      return false;
    },
  },
  mutations: {
    SET_TENANT_DETAILS(state, details) {
      state.tenantDetails = details;
    },
    SET_BOARD_COUNT(state, count) {
      state.boardCount = count;
    },
  },
  actions: {
    async fetchTenantDetails({ commit, getters }) {
      const tenant = getters["tenancy/currentTenant"];
      if (!tenant?.tenant_id) return;
      try {
        const res = await APIService.getTenant(tenant.tenant_id);
        commit("SET_TENANT_DETAILS", res.data);
      } catch (err) {
        console.error("Failed to fetch tenant details:", err);
      }
    },
    async fetchBoardCount({ commit, getters }) {
      try {
        const res = await APIService.getTables();
        const boards = res.data.filter((t) => getters.getTableViewType(t) === "kanban");
        commit("SET_BOARD_COUNT", boards.length);
      } catch {
        // If tables fail to load, don't block — count stays at 0
      }
    },
    async refreshTenantState({ dispatch }) {
      await Promise.all([dispatch("fetchTenantDetails"), dispatch("fetchBoardCount")]);
    },
  },
});
